import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const TomSelect = ({
  name,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  valueField = 'id',
  labelField = 'name',
  searchField = 'name',
  required = false,
  error = null,
  disabled = false,
  label = '',
  sortField = { field: 'name', direction: 'asc' },
  create = false,
  multiple = false,
  focusSignal = 0
}) => {
  const selectRef = useRef(null);
  const instanceRef = useRef(null);
  const onChangeScheduledRef = useRef(null);
  const isMountedRef = useRef(true);
  // Store the latest onChange callback in a ref to avoid stale closures
  const onChangeRef = useRef(onChange);
  const prevFocusSignalRef = useRef();

  // Update the onChange ref whenever it changes
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Init/destroy TomSelect once
  useEffect(() => {
    if (selectRef.current && window.TomSelect) {
      const plugins = multiple ? ['remove_button'] : [];

      instanceRef.current = new window.TomSelect(selectRef.current, {
        valueField,
        labelField,
        searchField,
        options: options || [],
        create,
        sortField,
        placeholder,
        plugins,
        // UX tweaks:
        closeAfterSelect: !multiple,
        openOnFocus: true,
        hideSelected: true,
        onChange: (val) => {
          console.log(`[TomSelect] onChange triggered for ${name}, value:`, val, 'type:', typeof val);
          // Use the ref to get the latest onChange callback (avoids stale closures)
          const currentOnChange = onChangeRef.current;
          
          // Clear any pending onChange callbacks
          if (onChangeScheduledRef.current) {
            clearTimeout(onChangeScheduledRef.current);
            onChangeScheduledRef.current = null;
          }
          
          // Schedule the React state update to next tick so TomSelect finishes its internal handling
          onChangeScheduledRef.current = setTimeout(() => {
            try {
              if (multiple) {
                const arr = Array.isArray(val) ? val : (val ? [val] : []);
                console.log(`[TomSelect] ${name} calling onChange with array:`, arr);
                if (currentOnChange) {
                  currentOnChange(name, arr);
                }
              } else {
                // Ensure we pass the value even if it's null/undefined (let parent handle it)
                console.log(`[TomSelect] ${name} calling onChange with value:`, val, 'type:', typeof val);
                if (currentOnChange) {
                  currentOnChange(name, val);
                }
              }
            } catch (error) {
              console.warn('TomSelect onChange error:', error);
            } finally {
              onChangeScheduledRef.current = null;
            }
          }, 0);
        }
      });
      
      // Set initial value if provided
      if (value !== null && value !== undefined && value !== '') {
        try {
          instanceRef.current.setValue(String(value), true);
        } catch (e) {
          console.warn(`[TomSelect] ${name} failed to set initial value:`, e);
        }
      }
    }

    return () => {
      // Clean up scheduled callbacks
      if (onChangeScheduledRef.current) {
        clearTimeout(onChangeScheduledRef.current);
        onChangeScheduledRef.current = null;
      }
      // Destroy TomSelect instance
      if (instanceRef.current) {
        try {
          instanceRef.current.destroy();
        } catch (e) {
          // ignore destroy errors
        }
        instanceRef.current = null;
      }
      // Mark as unmounted
      isMountedRef.current = false;
    };
    // We intentionally run this only once on mount/unmount
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update options when they change (safe)
  useEffect(() => {
    if (instanceRef.current) {
      try {
        instanceRef.current.clearOptions();
        if (Array.isArray(options) && options.length) {
          instanceRef.current.addOptions(options);
        }
      } catch (e) {
        // fail silently — TomSelect may be mid-update sometimes
      }
    }
  }, [options]);

  // Update value when it changes externally, but only if different
  useEffect(() => {
    if (!instanceRef.current) return;

    try {
      const inst = instanceRef.current;
      const currentRaw = inst.getValue(); // can be string or array depending on config

      if (multiple) {
        const currentArr = Array.isArray(currentRaw) ? currentRaw.map(String) : (currentRaw ? [String(currentRaw)] : []);
        const desiredArr = Array.isArray(value) ? value.map(String) : [];
        const same = currentArr.length === desiredArr.length &&
                     currentArr.every((v, i) => v === desiredArr[i]);
        if (!same) {
          // setValue silently (true)
          inst.setValue(desiredArr, true);
        }
      } else {
        const currentStr = currentRaw == null ? '' : String(currentRaw);
        const desiredStr = value == null ? '' : String(value);
        if (currentStr !== desiredStr) {
          inst.setValue(desiredStr, true);
        }
      }
    } catch (e) {
      // ignore errors from getValue/setValue
    }
  }, [value, multiple]);

  // Allow parent to request focus (e.g., after validation failures)
  useEffect(() => {
    const prev = prevFocusSignalRef.current;
    prevFocusSignalRef.current = focusSignal;

    // Only focus when parent explicitly increments the signal (non-zero and changed)
    if (!focusSignal || focusSignal === prev) return;
    if (!instanceRef.current) return;

    // Next tick so DOM is ready after modals/rerenders
    setTimeout(() => {
      try {
        instanceRef.current?.focus?.();
      } catch (e) {
        // ignore
      }
    }, 0);
  }, [focusSignal]);

  return (
    <div className="mb-3">
      {label && (
        <label className="form-label d-block">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}
      {/* hidden native select — TomSelect owns the visible DOM */}
      <select
        ref={selectRef}
        name={name}
        multiple={multiple}
        style={{ display: 'none' }}
        aria-hidden="true"
        disabled={disabled}
        // IMPORTANT: Do not render <option> children or control `value` from React.
        // TomSelect mutates the underlying select/options; letting React reconcile
        // those nodes can cause DOM exceptions like "removeChild ... not a child".
        onChange={() => {}}
      >
      </select>

      {/* validation message (always rendered by React) */}
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

TomSelect.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array
  ]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  valueField: PropTypes.string,
  labelField: PropTypes.string,
  searchField: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  sortField: PropTypes.object,
  create: PropTypes.bool,
  multiple: PropTypes.bool,
  focusSignal: PropTypes.number
};

export default TomSelect;
